// @flow
import { useSelector } from "react-redux";
import { css } from "styled-components";
import { useQuery } from "@apollo/client/react/hooks";
import { mediumParagraph, CalendlyWidget } from "@nested/brand";
import { RM_QUERY } from "./SignPost";
import { Heading, QuestionWrapper } from "../components";
import { type QuestionProps } from "../components/MultiStageForm";

const questionDescription = css`
  ${mediumParagraph}
  margin: 30px 0;
`;

const getCalendlySettings = (reasonForContact, rm) => {
  if (
    reasonForContact === "HOME_VISIT" ||
    reasonForContact === "SCHEDULE_A_VALUATION"
  ) {
    return {
      title: "When shall we meet?",
      description:
        "Let us know a convenient time for your local agent to come to your home for a valuation. We'll also talk you through selling with Nested, and answer any questions you might have.",
      calendlyUrl: rm.homeVisitBookingUrl,
    };
  }

  if (reasonForContact === "VIRTUAL_HOME_VISIT") {
    return {
      title: "When shall we have a virtual valuation call on Zoom?",
      description:
        "Let us know a convenient time for a virtual valuation call with your local agent. We'll also talk you through selling with Nested, and answer any questions you might have.",
      calendlyUrl: rm.virtualHomeVisitBookingUrl,
    };
  }

  return {
    title: "When can we call?",
    description:
      "Let us know a convenient time to chat. We'll talk you through selling with Nested, and answer any questions you might have.",
    calendlyUrl: rm.introCallBookingUrl,
  };
};

/*
 * If you're looking for the Calendly question used in the main onboarding flow, go to
 * CalendlyOnboarding instead. This question is for use in other forms (e.g. PersonalValuationForm)
 */
export const Calendly = ({ next }: QuestionProps<"calendly">) => {
  const formState = useSelector((state) => state.onboardingForm);
  const { address, email, name, phone, reasonForContact, contactDetails } =
    formState;
  const { data, loading } = useQuery(RM_QUERY, {
    variables: { postcode: address.postcode },
  });

  if (loading) {
    return null;
  }

  const rm = data?.postcodeDetails?.relationshipManager;

  const { calendlyUrl, title, description } = getCalendlySettings(
    reasonForContact,
    rm,
  );

  return (
    <QuestionWrapper>
      <Heading>{title}</Heading>
      <p css={questionDescription}>{description}</p>

      <CalendlyWidget
        email={contactDetails.email || email.email}
        name={contactDetails.name || `${name.firstName} ${name.lastName}`}
        phone={contactDetails.phone || phone}
        address={`${formState.address.label}, ${formState.address.postcode}`}
        url={calendlyUrl}
        onSuccess={() => next(true)}
        onFailure={() => next(false)}
        onSkip={() => next(false)}
      />
    </QuestionWrapper>
  );
};
